import {
  filter,
  flow,
  get,
  head,
  isEmpty,
  isNil,
  last,
  negate,
  size,
  toPairs,
  map,
} from 'lodash/fp';
import type { CondPairUnary } from 'lodash';
import { includes, partial } from 'lodash';
import type {
  HandleLinkClientToTestimonials,
  SetFormTypes,
  SetTestimonials,
  TestimonialsFormValues,
} from 'src/routes/TestimonialsForm/types';
import { TestimonialsFormTypes } from 'src/routes/TestimonialsForm/types';
import { CONTENT_MIN_LENGTH } from 'src/routes/TestimonialsForm/constants';
import * as yup from 'yup';
import type { CLIENT } from '@lt/api/dist/types/client';
import type {
  postTestimonialsCreateRequest,
  postTestimonialsCreateResponse,
  postTestimonialsLinkResponse,
  TestimonialsRatingValue,
} from '@lt/api/dist/methods/testimonials/types';
import type { AxiosError } from 'axios';
import { pluralize } from 'src/utils/stringUtils';

export const contentLength = flow<[TestimonialsFormValues], string, number>(
  get<TestimonialsFormValues, 'content'>(['content']),
  size,
);

const contentLengthLessThanMin = flow(
  contentLength,
  (len) => len < CONTENT_MIN_LENGTH,
);

export const titleIsEmpty = flow<[TestimonialsFormValues], string, boolean>(
  get<TestimonialsFormValues, 'title'>(['title']),
  isEmpty,
);
export const contentIsEmpty = flow<[TestimonialsFormValues], string, boolean>(
  get<TestimonialsFormValues, 'content'>(['content']),
  isEmpty,
);

export const ratingIsEmpty = flow(
  toPairs,
  filter(
    flow(
      head,
      partial(includes, ['tour_selection', 'booking_simplicity', 'usability']),
    ),
  ),
  map(last),
  filter(isNil),
  negate(isEmpty),
);

const isErrorCommentEmpty = isEmpty;

const getErrorComment = (values: TestimonialsFormValues) => {
  const errors: string[] = [];
  const chars_left = CONTENT_MIN_LENGTH - contentLength(values);

  if (titleIsEmpty(values)) {
    errors.push('заполнить заголовок');
  }

  if (contentLengthLessThanMin(values)) {
    const min_text = chars_left < CONTENT_MIN_LENGTH ? '' : 'минимум';
    errors.push(
      `написать ${min_text} ${chars_left} ${pluralize(chars_left, [
        'символ',
        'символа',
        'символов',
      ])}`,
    );
  }
  if (ratingIsEmpty(values)) {
    errors.push('проставить оценки');
  }
  if (errors.length) {
    const last_item = errors.length > 1 ? ` и ${errors.pop()}` : '';
    return `Осталось ${errors.join(', ')}${last_item}`;
  }

  return '';
};

export const getCommentFormFieldsToFill = (values: TestimonialsFormValues) => {
  if (titleIsEmpty(values) && contentIsEmpty(values) && ratingIsEmpty(values)) {
    return `Минимальная длина – ${CONTENT_MIN_LENGTH} символов и проставить оценки`;
  }

  const errors = getErrorComment(values);

  if (isErrorCommentEmpty(errors)) {
    return 'Отзыв готов к публикации!';
  }
  return errors;
};

export const testimonialsFormValidate = yup.object({
  title: yup
    .string()
    .required('Пожалуйста, расскажите о ваших впечатлениях от нашего сервиса'),
  content: yup
    .string()
    .required('Текст отзыва необходимо заполнить')
    .min(
      CONTENT_MIN_LENGTH,
      `Минимальная длина вашего отзыва должна составлять ${CONTENT_MIN_LENGTH} символов включая пробелы`,
    ),
  tour_selection: yup.string().required().oneOf(['1', '2', '3', '4', '5']),
  booking_simplicity: yup.string().required().oneOf(['1', '2', '3', '4', '5']),
  usability: yup.string().required().oneOf(['1', '2', '3', '4', '5']),
});

const isClientAuthAndConfirmPhone =
  (client: CLIENT | null, unconfirmedPhone: string | null) => () =>
    negate(isEmpty)(client) && !unconfirmedPhone;

export const ifClientAuth = (
  client: CLIENT | null,
  unconfirmedPhone: string | null,
  handleLink: HandleLinkClientToTestimonials,
  setFormTypes: SetFormTypes,
): CondPairUnary<
  postTestimonialsCreateResponse,
  Promise<postTestimonialsLinkResponse | AxiosError>
> => [
  isClientAuthAndConfirmPhone(client, unconfirmedPhone),
  (testimonials) => {
    setFormTypes(TestimonialsFormTypes.loading);
    return handleLink(testimonials.testimonial.id, client?.id as number);
  },
];

const isClientNotAuthOrUnconfirmPhone =
  (client: CLIENT | null, unconfirmedPhone: string | null) => () =>
    isEmpty(client) || !!unconfirmedPhone;

export const ifClientIsNotAuth = (
  client: CLIENT | null,
  unconfirmedPhone: string | null,
  setFormTypes: SetFormTypes,
  setTestimonials: SetTestimonials,
): CondPairUnary<postTestimonialsCreateResponse, undefined> => [
  isClientNotAuthOrUnconfirmPhone(client, unconfirmedPhone),
  (testimonials) => {
    setTestimonials(testimonials);
    setFormTypes(TestimonialsFormTypes.auth);
    throw new Error('client not auth');
  },
];

export const ifTestimonialsLinkSuccess = (
  setFormTypes: SetFormTypes,
): CondPairUnary<postTestimonialsLinkResponse, undefined> => [
  (response) => response?.success,
  () => {
    setFormTypes(TestimonialsFormTypes.done);
    return undefined;
  },
];

export const ifTestimonialsLinkNotSuccess = (
  setFormTypes: SetFormTypes,
): CondPairUnary<postTestimonialsLinkResponse, undefined> => [
  (response) => !response?.success,
  () => {
    setFormTypes(TestimonialsFormTypes.done);
    return undefined;
  },
];

export const mapTestimonialsFormValuesToRequest = (
  values: TestimonialsFormValues | undefined,
  // orderId?: string,
): postTestimonialsCreateRequest | undefined => {
  if (!values) {
    return undefined;
  }

  return {
    testimonial: {
      title: values?.title,
      content: values?.content,
      // order_id: orderId,

      booking_simplicity: values?.booking_simplicity as TestimonialsRatingValue,
      tour_selection: values?.tour_selection as TestimonialsRatingValue,
      usability: values?.usability as TestimonialsRatingValue,
    },
  };
};
